import {css, StyleSheet} from 'aphrodite';
import reactUpdate from 'immutability-helper';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {ProxiedNoteProps} from 'notebook/util/NoteUtilConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import * as AssayRecordActions from 'results/assays/AssayRecordActions';
import {AssayResult, DATA_MISSING} from 'results/assays/AssayRecordConstants';
import {DataMissing} from 'results/reduceResults';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import memoizeLastCall from 'util/memoizeLastCall';

import {DisplayConfig, NoteLinkedAssayData} from './AttachmentConstants';
import EmptyAssayDataMessage from './EmptyAssayDataMessage';
import NoteLinkedAssayResults from './NoteLinkedAssayResults';

/**
 * Return DATA_MISSING if any of the results are DATA_MISSING, otherwise a list of assay results
 */
function _extractAssayResults(
  assayResultsById: {[assayResultId: string]: AssayResult | DataMissing},
  ids: Array<string>
): Array<AssayResult> | string {
  // eslint-disable-next-line no-restricted-syntax
  const results = _.values(_.pick(assayResultsById, ids));
  if (results.some((result) => result === DATA_MISSING)) {
    return DATA_MISSING;
  } else {
    return results;
  }
}

/**
 * We need to memoize this function because _extractAssayResults is called by mapStateToProps and its
 * output is used to update the component's props. If the old props do not deep equal the new props, then
 * the component re-renders. Since _extractAssayResults creates a new object in each call, even if the values
 * are identical the component would unnecessarily rerender. Wrapping this in memoizeLastCall solves this by
 * returning the last value it computed if the arguements passed in are the same.
 */
const _memoizeExtractAssayResults = memoizeLastCall(
  (assayResultsById: {[assayResultId: string]: AssayResult | DataMissing}, ids: Array<string>) =>
    _extractAssayResults(assayResultsById, ids)
);

function areAssayResultsMissing(assayResults: Array<AssayResult> | string | null): assayResults is string {
  return assayResults === DATA_MISSING;
}

export function mapStateToProps(
  state: BenchlingState,
  ownProps: BaseProps
): {
  assayResults: Array<AssayResult> | null;
  dataMissing: boolean;
} {
  const {assayResultsById} = state.models.results;
  const loadedAssayResults = _memoizeExtractAssayResults(assayResultsById, ownProps.assayResultIds);
  const assayResults =
    !areAssayResultsMissing(loadedAssayResults) &&
    loadedAssayResults.length === ownProps.assayResultIds.length
      ? loadedAssayResults
      : null;

  return {
    dataMissing: loadedAssayResults === DATA_MISSING,
    assayResults,
  };
}

interface DispatchProps {
  onFetchAssayResults: () => void;
}

function mapDispatchToProps(dispatch: BenchlingDispatch, ownProps: BaseProps): DispatchProps {
  return {
    onFetchAssayResults(): void {
      dispatch(AssayRecordActions.fetchAssayResults(ownProps.assayResultIds));
    },
  };
}

interface ConnectedProps {
  assayResults: Array<AssayResult> | null;
  dataMissing: boolean;
}

interface BaseProps extends ProxiedNoteProps {
  registerObjects: (cidIdMap: CidIdMap) => void;
  storeState: () => void;
  updateHandler: (index: number, content: Partial<NoteLinkedAssayData>) => void;
  assayResultSchemas: Array<AssaySchema>;
  assayResultIds: Array<string>;
  displayConfigBySchemaId: {[schemaId: string]: DisplayConfig};
  editor: NoteEditor;
  editorProps: NoteEditorProps;
}

export type LegacyNoteLinkedAssayResultsFrameProps = BaseProps & ConnectedProps & DispatchProps;

export class LegacyNoteLinkedAssayResultsFrame extends React.Component<LegacyNoteLinkedAssayResultsFrameProps> {
  override componentDidMount(): void {
    this.props.onFetchAssayResults();
  }

  _handleUpdateDisplayConfig = (schemaId: string, newDisplayConfig: DisplayConfig): void => {
    this.props.storeState();
    this.props.updateHandler(this.props.index, {
      displayConfigBySchemaId: reactUpdate(this.props.displayConfigBySchemaId, {
        [schemaId]: {$set: newDisplayConfig},
      }),
    });
  };

  override render(): JSX.Element {
    if (this.props.dataMissing) {
      return (
        <EmptyAssayDataMessage indentation={this.props.indentation} title={`Attached ${MODEL_NAMES.Results}`}>
          <span>
            You do not have access to the attached assay data. The data may have been uploaded to a project
            you do not have read access to.
          </span>
        </EmptyAssayDataMessage>
      );
    }

    return (
      <div
        {...getTestProps({
          component: 'LegacyNoteLinkedAssayResultsFrame',
          element: 'root',
        })}
        className={css(styles.root)}
      >
        <NoteLinkedAssayResults
          assayResultSchemas={this.props.assayResultSchemas}
          assayResults={this.props.assayResults}
          displayConfigBySchemaId={this.props.displayConfigBySchemaId}
          handleUpdateDisplayConfig={this._handleUpdateDisplayConfig}
          cid={this.props.cid}
          fileId={this.props.fileId}
          indentation={this.props.indentation}
          index={this.props.index}
          locked={this.props.locked}
          viewer={this.props.viewer}
        />
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(LegacyNoteLinkedAssayResultsFrame);

const styles = StyleSheet.create({
  root: {display: 'flex'},
});
